/**
 * What the coach sees when she asks "what exercise goes here".
 *
 * Two pure functions, kept out of the component so they can be tested against
 * the real catalogue rather than against a handful of invented rows.
 *
 * The problem this exists to solve: a typeahead over the fourteen exercises a
 * coach has typed herself is fine, and the same typeahead over a stock
 * catalogue is not. It fails on **recall** — she says "hip thrust", the row is
 * called "Resistance band hip thrust" — and it fails on **ranking**, where
 * "press" returns every press in the catalogue in whatever order the database
 * handed them over. So:
 *
 *   * every query word must match *something*, but it may match the name, the
 *     equipment, the target muscle or the body part, and it matches on a word
 *     prefix rather than on the whole string. "hip thrust" therefore finds
 *     "Resistance band hip thrust", and "dumbbell glute" finds the dumbbell
 *     exercises that work the glutes without either word being in the name;
 *   * results are **scored and capped**. A word matched in the name beats the
 *     same word matched in a muscle list, an earlier match beats a later one,
 *     and a shorter name beats a longer one, so the plain movement sits above
 *     its variations. The cap is what stops a broad word being a wall: the
 *     caller is told how many rows were left out, which is the honest prompt
 *     to type another word.
 *
 * Her own exercises are not ranked against the stock ones. They are a separate
 * section pinned above, because "which of these is mine" is not a relevance
 * question (`exercise-catalogue`, "Her own exercises come first").
 */

/** One row the coach can pick, hers or the catalogue's. */
export interface PickerOption {
  /** Hers, or the catalogue's. Drives the section it appears in. */
  kind: 'mine' | 'stock';
  /** Stable within a list; used as the React key and in test queries. */
  key: string;
  name: string;
  /**
   * Her own library item, if this row is one. Null for a stock exercise and
   * null for a name she once typed by hand and that the recents list is
   * offering back to her — a name with nothing behind it is a real and common
   * case, and pretending otherwise would invent a library item per typo.
   */
  libraryItemId: string | null;
  /** The catalogue row behind this option, if there is one. */
  catalogId: string | null;
  /**
   * What the catalogue calls this movement, when she has renamed it.
   *
   * Searchable but not the headline. Once she has renamed "Barbell romanian
   * deadlift" to "RDL - slow eccentric", typing the catalogue's words has to
   * still find her row — otherwise adopting an exercise quietly makes it harder
   * to find than it was before she adopted it.
   */
  stockName: string | null;
  /** Our own media key, from the catalogue row. Null when there is none. */
  mediaKey: string | null;
  equipment: string | null;
  bodyPart: string | null;
  target: string | null;
  muscleGroup: string | null;
  secondaryMuscles: string[];
  /** Her standing cue, for her own rows. */
  cue: string | null;
}

/** What the picker shows, once a query has been applied. */
export interface PickerResults {
  mine: PickerOption[];
  stock: PickerOption[];
  /** Stock matches that did not fit under the cap. */
  omitted: number;
}

/** Lower-case, unaccented words. "Résistance band" -> ["resistance", "band"]. */
function words(text: string): string[] {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((word) => word !== '');
}

/** Everything about an option that a coach might reasonably type. */
function haystack(option: PickerOption): { name: string[]; meta: string[] } {
  return {
    name: words(option.name),
    meta: words(
      [
        option.stockName ?? '',
        option.equipment ?? '',
        option.bodyPart ?? '',
        option.target ?? '',
        option.muscleGroup ?? '',
        ...option.secondaryMuscles,
      ].join(' '),
    ),
  };
}

/**
 * How well one option answers one query. `null` means "not a match at all",
 * which is different from "a poor match": every word the coach typed has to
 * land somewhere, or she gets rows she did not ask for.
 */
function score(queryWords: string[], option: PickerOption): number | null {
  const { name, meta } = haystack(option);
  let total = 0;

  for (const word of queryWords) {
    const inName = name.findIndex((candidate) => candidate.startsWith(word));
    if (inName !== -1) {
      // A word in the name is worth far more than the same word in a muscle
      // list, and a word near the front of the name is worth more than one at
      // the end: "Barbell curl" should beat "Cable pulldown bicep curl" for
      // "curl".
      total += 100 - Math.min(inName, 9) * 5;
      if (name[inName] === word) total += 20; // a whole word, not a prefix
      continue;
    }

    if (meta.some((candidate) => candidate.startsWith(word))) {
      total += 25;
      continue;
    }

    return null;
  }

  // The whole name typed out wins outright, and a name that *starts* with what
  // she typed comes next — so "crunch" offers "Crunch" before "Cable kneeling
  // crunch", and typing the full "cable kneeling crunch" pins that one to the
  // top.
  const joined = name.join(' ');
  const query = queryWords.join(' ');
  if (joined === query) total += 1000;
  else if (joined.startsWith(query)) total += 200;

  // Among equally good matches, the plainer movement first.
  total -= name.length;

  return total;
}

/**
 * The coach's own exercises, then the stock ones, for a query.
 *
 * An empty query is not an empty screen: the caller passes the exercises she
 * used most recently and they come back untouched
 * (`exercise-catalogue`, "An empty field is not an empty screen").
 */
export function searchExercises(
  query: string,
  options: readonly PickerOption[],
  recents: readonly PickerOption[],
  limit = 12,
): PickerResults {
  const queryWords = words(query);

  if (queryWords.length === 0) {
    const shown = recents.slice(0, limit);
    return {
      mine: shown.filter((option) => option.kind === 'mine'),
      stock: shown.filter((option) => option.kind === 'stock'),
      omitted: 0,
    };
  }

  const scored: { option: PickerOption; points: number }[] = [];
  for (const option of options) {
    const points = score(queryWords, option);
    if (points !== null) scored.push({ option, points });
  }
  scored.sort((a, b) => b.points - a.points || a.option.name.localeCompare(b.option.name));

  const mine = scored.filter((row) => row.option.kind === 'mine').map((row) => row.option);
  const stock = scored.filter((row) => row.option.kind === 'stock').map((row) => row.option);
  const room = Math.max(limit - mine.length, 1);

  return {
    mine,
    stock: stock.slice(0, room),
    omitted: Math.max(stock.length - room, 0),
  };
}

/* ------------------------------------------------------------- building --- */

/** The catalogue columns the picker reads. Deliberately not the instructions. */
export interface CatalogueEntry {
  id: string;
  name: string;
  equipment: string;
  body_part: string;
  target: string;
  muscle_group: string;
  secondary_muscles: string[];
  media_key: string;
}

/** The coach's own exercise, as the picker needs it. */
export interface LibraryEntry {
  id: string;
  name: string;
  cue: string | null;
  catalog_id: string | null;
}

/**
 * One list, not two databases.
 *
 * A stock exercise the coach has already adopted appears **once**, as hers —
 * her name and her cue, with the catalogue row's muscles and equipment still
 * behind it so that searching by equipment finds it. The stock row it came from
 * is dropped rather than shown a second time
 * (`exercise-catalogue`, "Her own exercises come first").
 */
export function buildPickerOptions(
  library: readonly LibraryEntry[],
  catalogue: readonly CatalogueEntry[],
): PickerOption[] {
  const byId = new Map(catalogue.map((row) => [row.id, row]));
  const adopted = new Set(
    library.map((item) => item.catalog_id).filter((id): id is string => id !== null),
  );

  const mine: PickerOption[] = library.map((item) => {
    const stock = item.catalog_id === null ? undefined : byId.get(item.catalog_id);
    return {
      kind: 'mine',
      key: `mine:${item.id}`,
      name: item.name,
      libraryItemId: item.id,
      catalogId: item.catalog_id,
      stockName: stock === undefined || stock.name === item.name ? null : stock.name,
      mediaKey: stock?.media_key ?? null,
      equipment: stock?.equipment ?? null,
      bodyPart: stock?.body_part ?? null,
      target: stock?.target ?? null,
      muscleGroup: stock?.muscle_group ?? null,
      secondaryMuscles: stock?.secondary_muscles ?? [],
      cue: item.cue,
    };
  });

  const stock: PickerOption[] = catalogue
    .filter((row) => !adopted.has(row.id))
    .map((row) => ({
      kind: 'stock',
      key: `stock:${row.id}`,
      name: row.name,
      libraryItemId: null,
      catalogId: row.id,
      stockName: null,
      mediaKey: row.media_key,
      equipment: row.equipment,
      bodyPart: row.body_part,
      target: row.target,
      muscleGroup: row.muscle_group,
      secondaryMuscles: row.secondary_muscles,
      cue: null,
    }));

  return [...mine, ...stock];
}

/** One row of `listRecentEntries`. */
export interface RecentEntry {
  name: string;
  library_item_id: string | null;
}

/**
 * The exercises she used most recently, as picker rows.
 *
 * An entry that points at one of her library items comes back as that item, so
 * picking it links the new entry the same way. An entry she typed by hand comes
 * back as a name and nothing else — pickable, and creating nothing.
 */
export function recentOptions(
  recents: readonly RecentEntry[],
  options: readonly PickerOption[],
  limit = 8,
): PickerOption[] {
  const byLibraryItem = new Map(
    options
      .filter((option) => option.libraryItemId !== null)
      .map((option) => [option.libraryItemId, option] as const),
  );

  const seen = new Set<string>();
  const picked: PickerOption[] = [];

  for (const entry of recents) {
    const fingerprint = entry.library_item_id ?? entry.name.trim().toLowerCase();
    if (fingerprint === '' || seen.has(fingerprint)) continue;
    seen.add(fingerprint);

    const known =
      entry.library_item_id === null ? undefined : byLibraryItem.get(entry.library_item_id);
    picked.push(
      known ?? {
        kind: 'mine',
        key: `recent:${fingerprint}`,
        name: entry.name,
        libraryItemId: null,
        catalogId: null,
        stockName: null,
        mediaKey: null,
        equipment: null,
        bodyPart: null,
        target: null,
        muscleGroup: null,
        secondaryMuscles: [],
        cue: null,
      },
    );
    if (picked.length === limit) break;
  }

  return picked;
}
